import React from 'react';
import { getHotelBookability, getImageUrl } from '@koob/utils';
import { useSelectedType } from '../hooks/useTripStoreSelectors';
import TripHotelCardStats from './TripHotelCardStats';
import TripHotelCardAvailability from './TripHotelCardAvailability';
import TripHotelCardHeader from './TripHotelCardHeader';
import TripHotelCardStylesBadge from './TripHotelCardStylesBadge';
import TripHotelCardCta from './TripHotelCardCta';

interface TripHotelCardProps {
  hotel: any;
  addHotel: (hotel: any) => void;
  hotelWithPrice?: any;
  /** `'itinerary'` hides the accept button — the map itinerary view is read-only. */
  kind?: string;
  /** Name links out to the hotel's own page. Off by default (not every caller has that route). */
  isForTrip?: boolean;
  /** Locale query param for the `isForTrip` link. */
  locale?: string;
  isIncluded?: boolean;
  /** Own icon + wording; only called while `hotel.instantAvailability` is set. */
  renderInstantAvailabilityBadge?: () => React.ReactNode;
  /** The app formats currency and picks what a price line shows. Not called while blocked or included. */
  renderPrice: (hotelData: any) => React.ReactNode;
  hotelUnavailableLabel: React.ReactNode;
  minimumStayLabel: (count: number) => React.ReactNode;
  priceIncludedLabel: React.ReactNode;
  providedByLabel: React.ReactNode;
  continueLabel: React.ReactNode;
  selectDayLabel: React.ReactNode;
}

export default function TripHotelCard({
  hotel,
  addHotel,
  hotelWithPrice,
  kind,
  isForTrip = false,
  locale,
  isIncluded = false,
  renderInstantAvailabilityBadge,
  renderPrice,
  hotelUnavailableLabel,
  minimumStayLabel,
  priceIncludedLabel,
  providedByLabel,
  continueLabel,
  selectDayLabel,
}: TripHotelCardProps) {
  const selectedType = useSelectedType();

  const hotelData = hotelWithPrice
    ? Object.assign(hotelWithPrice, hotel)
    : hotel;

  const styles = hotelData?.styles?.filter((s: any) => !!s) ?? [];
  const { isBlocked, isMinStayUnmet, minimumStay } = getHotelBookability(
    hotelData,
    { isIncluded },
  );
  const name = hotelData?.name ?? hotelData?.displayName ?? '';

  return (
    <div>
      <div className="relative text-left bg-white rounded-2xl overflow-hidden border border-solid border-k-gray">
        <div className="relative">
          {hotelData?.instantAvailability && renderInstantAvailabilityBadge?.()}

          <TripHotelCardStylesBadge styles={styles} hotelId={hotelData?.id} />

          <img
            className="w-full h-40 object-cover bg-gray-100"
            src={getImageUrl(hotelData) ?? '/img/hotel-image-placeholder.png'}
            onError={(ev) =>
              ((ev.target as HTMLImageElement).src =
                '/img/hotel-image-placeholder.png')
            }
            alt=""
          />
        </div>
        <div className="px-4 py-2">
          <div className="flex justify-between gap-2">
            <div className="flex-1 min-w-0">
              <TripHotelCardHeader
                name={name}
                address={hotelData?.address}
                hotelId={hotelData?.id}
                isForTrip={isForTrip}
                locale={locale}
              />

              <TripHotelCardAvailability
                hotelData={hotelData}
                isBlocked={isBlocked}
                isMinStayUnmet={isMinStayUnmet}
                minimumStay={minimumStay}
                isIncluded={isIncluded}
                renderPrice={renderPrice}
                hotelUnavailableLabel={hotelUnavailableLabel}
                minimumStayLabel={minimumStayLabel}
                priceIncludedLabel={priceIncludedLabel}
              />
            </div>

            <TripHotelCardStats
              stars={hotelData?.stars}
              promotionsCount={
                hotelData?.promotions?.length ?? hotelData?.promotionsCount
              }
            />
          </div>

          <div className="my-1 text-xs text-gray-500/75 flex items-center justify-end space-x-1">
            <div>{providedByLabel}</div>
            <div className="font-medium truncate">
              {hotelData?.organization?.displayName || hotelData?.organization}
            </div>
          </div>

          <TripHotelCardCta
            name={name}
            kind={kind}
            selectedType={selectedType}
            isIncluded={isIncluded}
            isBlocked={isBlocked}
            onAdd={() => addHotel(hotelData)}
            continueLabel={continueLabel}
            selectDayLabel={selectDayLabel}
          />
        </div>
      </div>
    </div>
  );
}
